import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  CircularProgressProps,
  useTheme,
  useMediaQuery,
  LinearProgress,
} from '@mui/material';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { serverConfig } from '../../api/apiConfig';
import { findProfileByUser, filterChatgpt } from '../../graphqlClient/graphql';

const workerUrl = new URL(
  'https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js',
  import.meta.url,
).toString();

function CircularProgressWithLabel(
  props: CircularProgressProps & { value: number },
) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant="determinate" {...props} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          style={{ color: '#ffffff', fontWeight: 'bold' }}
          variant="caption"
          component="div"
        >{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

export interface ScoutProfileSheetModalProps {
  open: boolean;
  onClose: () => void;
  scout: any;
  profile?: any;
  image?: string;
}

const ScoutProfileSheetModal: React.FC<ScoutProfileSheetModalProps> = ({
  open,
  onClose,
  scout,
  profile,
  image,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState<any>(profile || {});
  const [aiData, setAiData] = useState<any>(null);
  const [finalResult, setFinalResult] = useState<string>('');
  const [limit, setLimit] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);
  const [openPdf, setOpenPdf] = useState(false);
  const [defaultPdf, setDefaultPdf] = useState<string>('');

  const makeBullets = (val: any) => {
    let resp = '';
    if (Array.isArray(val)) {
      for (let z = 0; z < val.length; z++) {
        const valor = val[z];
        if (typeof valor !== 'string') {
          resp += makeBullets(valor) + '<br>';
        } else {
          resp += `* ${valor} <br>`;
        }
      }
    } else {
      for (const i in val) {
        const valor = val[i];
        if (typeof valor !== 'string') {
          resp += '<h3>' + i + '</h3><br>' + makeBullets(valor);
        } else {
          resp += `* ${i}: ${valor} <br>`;
        }
      }
    }
    return resp;
  };

  const makeFinalResult = (res: any) => {
    if (!res) {
      setFinalResult('');
      setLimit(0);
      return;
    }

    let resObj: any;
    try {
      resObj = typeof res === 'string' ? JSON.parse(res) : res;
    } catch (e) {
      setFinalResult(typeof res === 'string' ? res : JSON.stringify(res));
      setLimit(80);
      return;
    }

    let rawGrade = parseFloat(String(resObj?.grade));
    let grade = 80;
    if (!isNaN(rawGrade)) {
      if (rawGrade <= 1) rawGrade = rawGrade * 100;
      else if (rawGrade <= 10) rawGrade = rawGrade * 10;
      grade = Math.round(rawGrade);
    }
    setLimit(grade);

    let final = '';
    if (resObj && typeof resObj === 'object') {
      for (const i in resObj) {
        if (i === 'grade') continue;
        const valor = resObj[i];
        final +=
          '<h3 style="color: #00FFFF; margin-top: 18px; margin-bottom: 8px; font-weight: 600; text-transform: capitalize; border-bottom: 1px solid rgba(138, 46, 226, 0.2); padding-bottom: 4px;">' +
          i +
          '</h3>';
        if (typeof valor === 'object') {
          final += makeBullets(valor);
        } else {
          final += valor;
        }
        final += '<br>';
      }
    }
    setFinalResult(final);
  };

  const loadData = async () => {
    if (!scout?._id) return;
    setLoading(true);

    try {
      // 1. Fetch Profile
      const fetchedProfile = await findProfileByUser(scout._id);
      const mergedProfile = { ...(fetchedProfile || {}), ...(profile || {}) };
      setProfileData(mergedProfile);

      const pdfFound = mergedProfile?.pdf || profile?.pdf || '';
      setDefaultPdf(pdfFound);

      // 2. Fetch AI Resume Feedback
      const aiResults = await filterChatgpt({
        user: scout._id,
        type: 'airesumefeedback',
      });

      if (aiResults && aiResults.length > 0) {
        const defaultRecord = aiResults.find((r: any) => r.isDefault === true);
        const activeRecord = defaultRecord || aiResults[aiResults.length - 1];

        setAiData(activeRecord);
        if (activeRecord.pdf && !pdfFound) {
          setDefaultPdf(activeRecord.pdf);
        }
        makeFinalResult(activeRecord.response);
      } else {
        setAiData(null);
        setFinalResult('');
        setLimit(0);
      }
    } catch (err) {
      console.warn('Error loading scout profile preview data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadData();
    } else {
      setLimit(0);
      setProgress(0);
      setFinalResult('');
      setAiData(null);
      setOpenPdf(false);
    }
  }, [open, scout?._id]);

  useEffect(() => {
    if (limit > 0) {
      let current = 0;
      const step = Math.ceil(limit / 20) || 1;
      const timer = setInterval(() => {
        current += step;
        if (current >= limit) {
          setProgress(limit);
          clearInterval(timer);
        } else {
          setProgress(current);
        }
      }, 25);
      return () => clearInterval(timer);
    } else {
      setProgress(0);
    }
  }, [limit]);

  const avatarSrc =
    image ||
    (scout?.image
      ? scout.image.startsWith('http')
        ? scout.image
        : `${serverConfig.dev.url}images/${scout.image}`
      : scout?.avatar ||
        (scout?.name
          ? `https://robohash.org/${scout.name}`
          : 'https://robohash.org/avatar'));

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            backgroundColor: '#0a0a0a',
            color: '#ffffff',
            width: isMobile ? '95%' : '90%',
            maxWidth: '1350px',
            maxHeight: '90vh',
            borderRadius: '16px',
            border: '1px solid rgba(138, 46, 226, 0.3)',
            boxShadow: '0 8px 32px 0 rgba(0, 255, 255, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            outline: 'none',
            fontFamily: '"Outfit", "Inter", sans-serif',
            overflow: 'hidden',
          }}
        >
          {loading && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                sx={{
                  backgroundColor: 'rgba(138, 46, 226, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #8A2BE2, #00FFFF)',
                  },
                }}
              />
            </Box>
          )}

          {/* Modal Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 24px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              backgroundColor: '#111115',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Avatar
                src={avatarSrc}
                style={{
                  width: '48px',
                  height: '48px',
                  border: '2px solid #00FFFF',
                  boxShadow: '0 0 10px rgba(0, 255, 255, 0.3)',
                }}
              />
              <div style={{ textAlign: 'left' }}>
                <h2
                  style={{
                    margin: 0,
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#ffffff',
                  }}
                >
                  {scout?.name || 'Unknown Scout'}
                </h2>
                <span
                  style={{ fontSize: '14px', color: '#00FFFF', fontWeight: 500 }}
                >
                  {profileData?.professionalTitle || 'No Title'}
                </span>
              </div>
            </div>
            <Button
              onClick={onClose}
              style={{
                color: '#ffffff',
                borderColor: 'rgba(255,255,255,0.3)',
                backgroundColor: 'rgba(255,255,255,0.05)',
                padding: '6px 16px',
                textTransform: 'uppercase',
                fontWeight: 600,
                fontSize: '12px',
              }}
              variant="outlined"
            >
              Close
            </Button>
          </div>

          {/* Modal Content - Scrollable */}
          <div
            style={{
              padding: '24px',
              overflowY: 'auto',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              backgroundColor: '#0a0a0a',
            }}
          >
            {/* Dynamic Two-Column Layout */}
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '24px',
                width: '100%',
                alignItems: 'stretch',
              }}
            >
              {/* Left Column: Personal and Professional Info */}
              <div
                style={{
                  flex: isMobile ? '1 1 100%' : '0 0 38%',
                  maxWidth: isMobile ? '100%' : '38%',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                }}
              >
                {/* Professional Details Section */}
                <div
                  style={{
                    backgroundColor: '#111115',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid rgba(138, 46, 226, 0.15)',
                    textAlign: 'left',
                  }}
                >
                  <h3
                    style={{
                      margin: '0 0 16px 0',
                      color: '#8A2BE2',
                      fontSize: '18px',
                      fontWeight: 600,
                      borderBottom: '1px solid rgba(138, 46, 226, 0.2)',
                      paddingBottom: '8px',
                    }}
                  >
                    Candidate Details
                  </h3>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        paddingBottom: '8px',
                      }}
                    >
                      <span style={{ color: '#a0a0ab', fontSize: '14px' }}>
                        Nowdays Location
                      </span>
                      <span style={{ color: '#ffffff', fontWeight: 500 }}>
                        {profileData?.location || 'N/A'}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        paddingBottom: '8px',
                      }}
                    >
                      <span style={{ color: '#a0a0ab', fontSize: '14px' }}>
                        Origin Location
                      </span>
                      <span style={{ color: '#ffffff', fontWeight: 500 }}>
                        {scout?.country || 'N/A'}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        paddingBottom: '8px',
                      }}
                    >
                      <span style={{ color: '#a0a0ab', fontSize: '14px' }}>
                        Years of Experience
                      </span>
                      <span style={{ color: '#ffffff', fontWeight: 500 }}>
                        {profileData?.yearsOfExperience
                          ? `${profileData.yearsOfExperience} years`
                          : 'N/A'}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        paddingBottom: '8px',
                      }}
                    >
                      <span style={{ color: '#a0a0ab', fontSize: '14px' }}>
                        English Level
                      </span>
                      <span
                        style={{
                          color: '#00FFFF',
                          border: '1px solid #00FFFF',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        {scout?.englishLevel || 'N/A'}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        marginTop: '8px',
                      }}
                    >
                      {scout?.employed ? (
                        <span
                          style={{
                            color: '#FF00FF',
                            backgroundColor: 'rgba(255, 0, 255, 0.1)',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            border: '1px solid #FF00FF',
                            fontSize: '12px',
                            fontWeight: 600,
                          }}
                        >
                          Employed
                        </span>
                      ) : (
                        <span
                          style={{
                            color: '#39FF14',
                            backgroundColor: 'rgba(57, 255, 20, 0.1)',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            border: '1px solid #39FF14',
                            fontSize: '12px',
                            fontWeight: 600,
                          }}
                        >
                          Open to Hire
                        </span>
                      )}
                      {scout?.opentorelocation && (
                        <span
                          style={{
                            color: '#00FFFF',
                            backgroundColor: 'rgba(0, 255, 255, 0.1)',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            border: '1px solid #00FFFF',
                            fontSize: '12px',
                            fontWeight: 600,
                          }}
                        >
                          Open to Relocate
                        </span>
                      )}
                      {scout?.uePassport && (
                        <span
                          style={{
                            color: '#FFFF00',
                            backgroundColor: 'rgba(255, 255, 0, 0.1)',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            border: '1px solid #FFFF00',
                            fontSize: '12px',
                            fontWeight: 600,
                          }}
                        >
                          UE Passport
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bio Summary Section */}
                <div
                  style={{
                    backgroundColor: '#111115',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid rgba(138, 46, 226, 0.15)',
                    textAlign: 'left',
                  }}
                >
                  <h3
                    style={{
                      margin: '0 0 12px 0',
                      color: '#8A2BE2',
                      fontSize: '18px',
                      fontWeight: 600,
                      borderBottom: '1px solid rgba(138, 46, 226, 0.2)',
                      paddingBottom: '8px',
                    }}
                  >
                    Bio / Professional Summary
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: '#e4e4e7',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {profileData?.bio ||
                      'No bio summary provided by the candidate.'}
                  </p>
                </div>

                {/* Experience & Education Details */}
                <div
                  style={{
                    backgroundColor: '#111115',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid rgba(138, 46, 226, 0.15)',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  <div>
                    <h3
                      style={{
                        margin: '0 0 8px 0',
                        color: '#8A2BE2',
                        fontSize: '16px',
                        fontWeight: 600,
                      }}
                    >
                      Education Background
                    </h3>
                    <p
                      style={{
                        margin: 0,
                        color: '#e4e4e7',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {profileData?.educationBackground ||
                        'No education background listed.'}
                    </p>
                  </div>
                  <div
                    style={{
                      borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                      paddingTop: '12px',
                    }}
                  >
                    <h3
                      style={{
                        margin: '0 0 8px 0',
                        color: '#8A2BE2',
                        fontSize: '16px',
                        fontWeight: 600,
                      }}
                    >
                      Experience Description
                    </h3>
                    <p
                      style={{
                        margin: 0,
                        color: '#e4e4e7',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {profileData?.description ||
                        'No detailed experience listed.'}
                    </p>
                  </div>
                </div>

                {/* Curriculum PDF */}
                <div
                  style={{
                    backgroundColor: '#111115',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid rgba(138, 46, 226, 0.15)',
                    textAlign: 'left',
                  }}
                >
                  <h3
                    style={{
                      margin: '0 0 12px 0',
                      color: '#8A2BE2',
                      fontSize: '18px',
                      fontWeight: 600,
                      borderBottom: '1px solid rgba(138, 46, 226, 0.2)',
                      paddingBottom: '8px',
                    }}
                  >
                    Curriculum Document
                  </h3>
                  {defaultPdf || profileData?.pdf ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      <span style={{ fontSize: '13px', color: '#a0a0ab' }}>
                        Uploaded CV: {defaultPdf || profileData?.pdf}
                      </span>
                      <Button
                        variant="contained"
                        onClick={() => setOpenPdf(true)}
                        style={{
                          background:
                            'linear-gradient(45deg, #8A2BE2, #00FFFF)',
                          color: '#ffffff',
                          fontWeight: 600,
                          textTransform: 'none',
                        }}
                      >
                        Open Curriculum Viewer
                      </Button>
                    </div>
                  ) : (
                    <span style={{ fontSize: '14px', color: '#a0a0ab' }}>
                      No resume PDF uploaded.
                    </span>
                  )}
                </div>
              </div>

              {/* Right Column: AI Resume Feedback Analysis (Favorite) */}
              <div
                style={{
                  flex: isMobile ? '1 1 100%' : '1 1 62%',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                }}
              >
                {/* Favorite AI Feedback Card */}
                <div
                  style={{
                    backgroundColor: '#111115',
                    borderRadius: '12px',
                    padding: '24px',
                    border: '1px solid rgba(0, 255, 255, 0.3)',
                    boxShadow: '0 0 15px rgba(0, 255, 255, 0.05)',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minWidth: 0,
                  }}
                >
                  {/* Header: AI Matching Feedback */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: '1px solid rgba(0, 255, 255, 0.2)',
                      paddingBottom: '12px',
                      marginBottom: '16px',
                      flexWrap: 'wrap',
                      gap: '12px',
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          color: '#00FFFF',
                          fontSize: '18px',
                          fontWeight: 600,
                        }}
                      >
                        AI Match Analysis
                      </h3>
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#39FF14',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          display: 'block',
                          marginTop: '2px',
                        }}
                      >
                        ★ Starred Feedback
                      </span>
                    </div>

                    {/* Animated/Glowing Matching Circular Score */}
                    {limit ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <CircularProgressWithLabel
                          value={progress}
                          size={50}
                          style={{ color: '#39FF14' }}
                        />
                        <span
                          style={{
                            fontSize: '12px',
                            color: '#a0a0ab',
                            fontWeight: 500,
                          }}
                        >
                          Compatibility Score
                        </span>
                      </div>
                    ) : null}
                  </div>

                  {/* AI Target Job Context */}
                  {aiData?.job && (
                    <div
                      style={{
                        marginBottom: '16px',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        borderLeft: '3px solid #FF00FF',
                        fontSize: '13px',
                        color: '#e4e4e7',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      <strong style={{ color: '#FF00FF' }}>
                        Target Job Description:
                      </strong>{' '}
                      {aiData.job}
                    </div>
                  )}

                  {/* Formatted Response Body */}
                  <div
                    style={{
                      flex: 1,
                      overflowY: 'auto',
                      maxHeight: '620px',
                      paddingRight: '8px',
                      fontSize: '14px',
                      color: '#e4e4e7',
                      lineHeight: '1.6',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                    className="custom-scrollbar"
                  >
                    {finalResult ? (
                      <div
                        dangerouslySetInnerHTML={{ __html: finalResult }}
                      />
                    ) : (
                      <div
                        style={{
                          color: '#a0a0ab',
                          textAlign: 'center',
                          padding: '40px 0',
                        }}
                      >
                        No starred AI Resume Feedback available for this profile.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* PDF Viewer Modal */}
      <Modal
        open={openPdf}
        onClose={() => setOpenPdf(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1400,
        }}
      >
        <div
          style={{
            margin: 0,
            backgroundColor: '#1a1a1a',
            width: isMobile ? '95%' : '80%',
            height: '85vh',
            maxHeight: '90vh',
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            border: '1px solid rgba(0, 255, 255, 0.2)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '10px 16px',
              backgroundColor: '#111115',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <Button
              onClick={() => setOpenPdf(false)}
              variant="outlined"
              style={{
                color: '#ffffff',
                borderColor: 'rgba(255,255,255,0.3)',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Close
            </Button>
          </div>
          <div style={{ flex: 1, overflow: 'hidden', backgroundColor: '#ffffff' }}>
            {defaultPdf || profileData?.pdf ? (
              <Worker workerUrl={workerUrl}>
                <Viewer
                  fileUrl={
                    serverConfig.dev.url +
                    'images/pdf/' +
                    (defaultPdf || profileData?.pdf)
                  }
                  plugins={[defaultLayoutPluginInstance]}
                />
              </Worker>
            ) : null}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ScoutProfileSheetModal;
